import React from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { getWorkoutStateAppearance } from "../../designSystem/chatSemanticColors";
import type {
  WorkoutDetailsPayload,
  WorkoutExerciseGroupItem,
  WorkoutSetColumnKey,
  WorkoutSetRow,
} from "./types";
import classes from "./WorkoutDetailsCard.module.css";

type Props = {
  data: WorkoutDetailsPayload;
};

const WorkoutDetailsCard = ({ data }: Props) => {
  const theme = useTheme();
  const progressPercentage = getProgressPercentage(
    data.calories?.current,
    data.calories?.target
  );

  return (
    <article className={classes.card}>
      <header className={classes.header}>
        <div>
          <p className={classes.eyebrow}>
            {data.isScheduled
              ? data.ui.workoutTypeLabels.scheduled
              : data.ui.workoutTypeLabels.logged}
          </p>
          <h4 className={classes.title}>{data.title}</h4>
          <p className={classes.subtitle}>
            {data.dayLabel} · {data.activityType}
          </p>
        </div>
        {data.stateTag ? (
          <span
            className={classes.stateTag}
            style={getWorkoutStateAppearance(theme, data.stateTag)}
          >
            {data.ui.stateLabels[data.stateTag]}
          </span>
        ) : null}
      </header>

      {data.calories ? (
        <section className={classes.caloriesCard}>
          <div className={classes.caloriesHeader}>
            <span className={classes.caloriesLabel}>{data.ui.calories.label}</span>
            <span className={classes.caloriesValue}>{data.calories.current}</span>
          </div>
          {typeof data.calories.target === "number" ? (
            <>
              <div className={classes.progressTrack}>
                <div
                  className={classes.progressBar}
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <div className={classes.caloriesTarget}>
                {data.ui.calories.targetPrefix} {data.calories.target}{" "}
                {data.ui.calories.targetSuffix}
              </div>
            </>
          ) : null}
        </section>
      ) : null}

      {data.summaryMetrics.length > 0 ? (
        <section className={classes.section}>
          <h5 className={classes.sectionTitle}>{data.ui.sectionTitles.overview}</h5>
          <div className={classes.metricGrid}>
            {data.summaryMetrics.map((item) => (
              <div key={`${item.label}-${item.value}`} className={classes.metricCard}>
                <span className={classes.metricLabel}>{item.label}</span>
                <span className={classes.metricValue}>{item.value}</span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {data.wearableData?.length ? (
        <section className={classes.section}>
          <h5 className={classes.sectionTitle}>
            {data.ui.sectionTitles.wearableData}
          </h5>
          <div className={classes.wearableList}>
            {data.wearableData.map((event) => (
              <article key={event.id} className={classes.wearableCard}>
                <div className={classes.wearableTitleRow}>
                  <p className={classes.wearableTitle}>{event.title}</p>
                  <span className={classes.wearableSource}>{event.sourceLabel}</span>
                </div>
                <div className={classes.wearableMeta}>
                  <span>
                    {event.calories} {data.ui.wearable.caloriesUnitSuffix}
                  </span>
                  <span>
                    {event.durationMinutes} {data.ui.wearable.durationUnitSuffix}
                  </span>
                  <span>{event.startedAtLabel}</span>
                  <span>
                    {event.hasHeartRateData
                      ? data.ui.wearable.hasHeartRateDataLabel
                      : data.ui.wearable.noHeartRateDataLabel}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {data.exerciseGroups?.length ? (
        <section className={classes.section}>
          <h5 className={classes.sectionTitle}>
            {data.ui.sectionTitles.exerciseGroups}
          </h5>
          {data.exerciseGroups.map((group, index) => (
            <ExerciseGroupCard
              key={group.id}
              group={group}
              defaultOpen={index === 0}
              ui={data.ui}
            />
          ))}
        </section>
      ) : null}
    </article>
  );
};

const ExerciseGroupCard = (props: {
  group: WorkoutExerciseGroupItem;
  defaultOpen: boolean;
  ui: WorkoutDetailsPayload["ui"];
}) => {
  return (
    <details className={classes.groupDetails} open={props.defaultOpen}>
      <summary className={classes.groupSummary}>
        <div>
          <p className={classes.groupTitle}>{props.group.typeLabel}</p>
          <p className={classes.groupDescription}>{props.group.description}</p>
        </div>
        <div className={classes.groupMeta}>
          {typeof props.group.totalMinutes === "number" ? (
            <span className={classes.groupMetaPill}>
              {props.group.totalMinutes} {props.ui.groupMeta.minutesSuffix}
            </span>
          ) : null}
          {typeof props.group.totalRounds === "number" ? (
            <span className={classes.groupMetaPill}>
              {props.group.totalRounds} {props.ui.groupMeta.roundsSuffix}
            </span>
          ) : null}
        </div>
      </summary>
      <div className={classes.groupBody}>
        {props.group.exercises.map((exercise) => (
          <article key={exercise.id} className={classes.exerciseCard}>
            <div className={classes.exerciseHeader}>
              {exercise.imageUrl ? (
                <div className={classes.exerciseImageWrap}>
                  <img
                    src={exercise.imageUrl}
                    alt={exercise.title}
                    className={classes.exerciseImage}
                    loading="lazy"
                  />
                </div>
              ) : null}
              <div>
                <h6 className={classes.exerciseTitle}>{exercise.title}</h6>
                {exercise.isSkipped ? (
                  <span className={classes.exerciseState}>
                    {props.ui.exerciseSkippedLabel}
                  </span>
                ) : null}
              </div>
            </div>

            <SetSection
              title={props.ui.setSections.prescribed}
              rows={exercise.prescribedSets}
              ui={props.ui}
            />
            {exercise.loggedSets?.length ? (
              <SetSection
                title={props.ui.setSections.logged}
                rows={exercise.loggedSets}
                ui={props.ui}
              />
            ) : null}
          </article>
        ))}
      </div>
    </details>
  );
};

const SetSection = (props: {
  title: string;
  rows: WorkoutSetRow[];
  ui: WorkoutDetailsPayload["ui"];
}) => {
  const columns = React.useMemo(
    () => getVisibleColumns(props.rows, props.ui),
    [props.rows, props.ui]
  );

  return (
    <section className={classes.setSection}>
      <p className={classes.setTitle}>{props.title}</p>
      <div className={classes.setTableWrap}>
        <table className={classes.setTable}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {props.rows.map((row) => (
              <tr key={row.index}>
                {columns.map((column) => (
                  <td key={column.key}>{column.render(row)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

function getVisibleColumns(
  rows: WorkoutSetRow[],
  ui: WorkoutDetailsPayload["ui"]
) {
  const placeholder = ui.setTable.placeholderValue;
  const label = (key: WorkoutSetColumnKey) => ui.setTable.columnLabels[key];
  const maybeColumns = [
    {
      key: "index",
      label: label("index"),
      render: (row: WorkoutSetRow) => row.index,
      shouldShow: (_row: WorkoutSetRow) => true,
    },
    {
      key: "reps",
      label: label("reps"),
      render: (row: WorkoutSetRow) => row.reps ?? placeholder,
      shouldShow: (row: WorkoutSetRow) => typeof row.reps === "number",
    },
    {
      key: "weightKg",
      label: label("weightKg"),
      render: (row: WorkoutSetRow) => row.weightKg ?? placeholder,
      shouldShow: (row: WorkoutSetRow) => typeof row.weightKg === "number",
    },
    {
      key: "recoverySeconds",
      label: label("recoverySeconds"),
      render: (row: WorkoutSetRow) =>
        typeof row.recoverySeconds === "number"
          ? `${row.recoverySeconds}${ui.setTable.recoverySecondsSuffix}`
          : placeholder,
      shouldShow: (row: WorkoutSetRow) => typeof row.recoverySeconds === "number",
    },
    {
      key: "workTimeMinutes",
      label: label("workTimeMinutes"),
      render: (row: WorkoutSetRow) =>
        typeof row.workTimeMinutes === "number"
          ? row.workTimeMinutes
          : placeholder,
      shouldShow: (row: WorkoutSetRow) => typeof row.workTimeMinutes === "number",
    },
    {
      key: "workZone",
      label: label("workZone"),
      render: (row: WorkoutSetRow) =>
        typeof row.workZone === "number" ? row.workZone : placeholder,
      shouldShow: (row: WorkoutSetRow) => typeof row.workZone === "number",
    },
    {
      key: "watts",
      label: label("watts"),
      render: (row: WorkoutSetRow) => row.watts ?? placeholder,
      shouldShow: (row: WorkoutSetRow) => typeof row.watts === "number",
    },
    {
      key: "speed",
      label: label("speed"),
      render: (row: WorkoutSetRow) => row.speed ?? placeholder,
      shouldShow: (row: WorkoutSetRow) => typeof row.speed === "number",
    },
    {
      key: "rpm",
      label: label("rpm"),
      render: (row: WorkoutSetRow) => row.rpm ?? placeholder,
      shouldShow: (row: WorkoutSetRow) => typeof row.rpm === "number",
    },
    {
      key: "stages",
      label: label("stages"),
      render: (row: WorkoutSetRow) => row.stages ?? placeholder,
      shouldShow: (row: WorkoutSetRow) => typeof row.stages === "number",
    },
  ];

  return maybeColumns.filter((column) =>
    rows.some((row) => column.shouldShow(row))
  );
}

function getProgressPercentage(current?: number, target?: number) {
  if (typeof current !== "number" || typeof target !== "number" || target <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(100, Math.round((current / target) * 100)));
}

export default WorkoutDetailsCard;
